/* eslint-disable react-hooks/exhaustive-deps */
import { Pagination } from '@eagle/api-types';
import { Feature, FeatureType, Thing, ThingEventSnapshot } from '@eagle/core-data-types';
import { FeatureTypes, locationRelatedEventTypes } from '@eagle/data-function-types';
import { AppliedFilter, CacheDataTypes, EVENT_DATA_MAX_DAYS, EventsTable, FILTER_SELECT_ALL_FLAG, FindItemsDeferredPaginatedResponse, getEventLabelWithFlag, Icon, MiddleSpinner, Query, SelectionCheckBox, SelectionCheckBoxGroup, useAuthenticated, useBoolFlag, useFetchAllCache, useFlags, usePromise, useSmallScreen } from '@eagle/react-common';
import { Alert, Box, Card, CardContent, Chip, FormControl, InputLabel, MenuItem, Select, Stack, styled, Typography } from '@ethings/ui-toolkit';
import Axios from 'axios';
import { DateTime } from 'luxon';
import { Dispatch, FC, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EVENT_DATA_MAX_DAYS_FLAG } from '../thing-detail';
import { ThingEventBase } from './thing-event-base';
import { ThingEventInvestigateButton } from './thing-event-investigate-button';
import { simplifyThingEventFilter, ThingEventFilter } from './thing-event.utils';

const StyledCard = styled(Card)({
  '& .MuiCardContent-root': {
    '&:last-child': {
      paddingTop: 0,
      paddingBottom: 0,
    },
    padding: 0,
  },
  padding: 0,
});

const StyledChip = styled(Chip)({
  maxWidth: '100%',
});

const StyledEventFilterSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== '$smallScreen',
})<{ $smallScreen: boolean }>(({ $smallScreen }) => ({
  width: $smallScreen ? '100%' : 250,
}));

export const DataDrivenThingEvents: FC<{ thing: Thing }> = ({ thing }) => {
  const { restClient } = useAuthenticated();
  const [activeFilters, setActiveFilters] = useState<ThingEventFilter[]>([]);
  const { t } = useTranslation(['common']);
  const hasTrackThingHistoryFlag = useBoolFlag('track-thing-history-feature');
  const flags = useFlags();
  const maxDays = flags[EVENT_DATA_MAX_DAYS_FLAG] as number || EVENT_DATA_MAX_DAYS;

  const myFeatureCache = useFetchAllCache(CacheDataTypes.MY_FEATURE);
  const featureTypeCache = useFetchAllCache(CacheDataTypes.FEATURE_TYPE);
  const [myFeatures, myFeaturesError, myFeaturesState] = usePromise<Feature[]>(
    myFeatureCache.all(),
    [myFeatureCache],
  );
  const [featureInstances, featureInstancesError, featureInstancesState] = usePromise<string[]>(
    async () => {
      const result = await restClient.my.featureInstances.getAll();
      return result.sort();
    },
    [restClient],
  );
  const [featureTypes, featuresError, featuresState] = usePromise<FeatureType[]>(
    featureTypeCache.all(),
    [featureTypeCache],
  );

  const handleQueryChanged = useCallback(({ filters }: Query, pagination: Pagination): FindItemsDeferredPaginatedResponse<ThingEventSnapshot> => {
    const cancelToken = Axios.CancelToken.source();
    return {
      cancel: () => cancelToken.cancel(),
      promise: restClient.thingEvent.getWithThing(thing._id, {
        dateRangeStart: DateTime.now().minus({ days: maxDays }).toUTC().toJSDate(),
        filter: { '$or': simplifyThingEventFilter(filters as unknown as ThingEventFilter[]) },
        limit: 5,
        skip: pagination.skip,
        sort: JSON.stringify({ occurred: -1 }),
      }, { cancelToken: cancelToken.token }),
    };
  }, [restClient]);

  if ([myFeaturesState, featureInstancesState, featuresState].includes('pending')) {
    return (
      <Card>
        <CardContent>
          <MiddleSpinner />
        </CardContent>
      </Card>
    );
  }

  const hasError = myFeaturesError || featureInstancesError || featuresError;

  return (
    <StyledCard>
      <CardContent>
        <ThingEventBase
          isFiltering={activeFilters.length > 0}
          headerContent={
            <DataDrivenThingEventFilter
              activeFilters={activeFilters}
              setActiveFilters={setActiveFilters}
              features={myFeatures ?? []}
              featureTypes={featureTypes ?? []}
              featureInstances={featureInstances ?? []}
            />
          }
          maxDays={maxDays}
        >
          {hasError ? (
            <Box m={2}>
              <Alert severity='error'>{t('common.hint.error')}</Alert>
            </Box>
          ) : (
            <EventsTable
              appliedFilters={activeFilters as unknown as AppliedFilter[]}
              fixHeight={true}
              hideCategory
              onQueryChanged={handleQueryChanged}
              rowChildren={hasTrackThingHistoryFlag ? (
                (event) => (
                  <ThingEventInvestigateButton
                    event={event}
                  />
                )
              ) : undefined}
              thing={thing}
            />
          )}
        </ThingEventBase>
      </CardContent>
    </StyledCard>
  );
};

const MAX_NUMBER_OF_CHIPS = 3;

interface DataDrivenThingEventFilterProps {
  activeFilters: ThingEventFilter[];
  setActiveFilters: Dispatch<SetStateAction<ThingEventFilter[]>>;
  features: Feature[];
  featureTypes: FeatureType[];
  featureInstances: string[];
}

const DataDrivenThingEventFilter: FC<DataDrivenThingEventFilterProps> = ({ activeFilters, setActiveFilters, features, featureTypes, featureInstances }) => {
  const smallScreen = useSmallScreen();
  const { t, i18n } = useTranslation(['common', 'track']);
  const hasFilterSelectAllFlag = useBoolFlag(FILTER_SELECT_ALL_FLAG);
  const flagUseEventV3 = useBoolFlag('portals-translation-retrieval-hook-for-event-and-alert-descriptions-feature');

  const options = useMemo(() => {
    const output = [];

    for (const instance of featureInstances) {
      const [featureId, ...instanceParts] = instance.split('/');
      const instanceName = instanceParts.join('.');
      const instanceKey = `common:feature-instances.${featureId}.${instanceName}`;

      const feature = features.find((feature) => feature._id === featureId);
      const featureType = featureTypes.find((featureType) => featureType._id === feature?.featureTypeId);
      if (!featureType) {
        continue;
      }
      const events = Object.keys(featureType.events)
        .filter((event) => !locationRelatedEventTypes.includes(event))
        .sort((a, b) => {
          console.log(featureType._id, a, instance);
          return getEventLabelWithFlag(featureType._id, a, instance, flagUseEventV3).localeCompare(getEventLabelWithFlag(featureType._id, b, instance, flagUseEventV3));
        });
      output.push({ feature: instance, featureTypeId: featureType._id, events, label: i18n.exists(instanceKey) ? t(instanceKey) : t(`common:features.${instance}`) });
    }

    return output.sort((a, b) => a.label.localeCompare(b.label));
  }, [featureInstances, features, featureTypes, t]);

  const getSelectedLabel = (event: ThingEventFilter): string => {
    const [featureId, ...instanceParts] = event.feature.split('/');
    const instanceName = instanceParts.join('.');
    const instanceKey = `common:feature-instances.${featureId}.${instanceName}`;
    const featureLabel = i18n.exists(instanceKey) ? t(instanceKey) : t(`common:features.${event.feature}`);
    const eventLabel = getEventLabelWithFlag(event.featureTypeId, event.eventTypeId, event.feature, flagUseEventV3);

    return event.featureTypeId === FeatureTypes.EVENT_RECORD_V0 ? `${featureLabel} – ${eventLabel}` : `${eventLabel} – ${featureLabel}`;
  };

  const numberOfOptions = options.reduce((result, option) => result + option.events.length, 0);
  const isAllSelected = activeFilters.length === numberOfOptions;

  const toggleSelectAll = (): void => {
    if (isAllSelected) {
      setActiveFilters([]);
      return;
    }
    const allValues = options.flatMap((option) => option.events.map((event) => ({
      feature: option.feature,
      eventTypeId: event,
      featureTypeId: option.featureTypeId,
    })));
    setActiveFilters(allValues);
  };

  return (
    <FormControl size='small'>
      {activeFilters.length === 0 && <InputLabel shrink={false}>{t('common:component.events.labels.event-filter')}</InputLabel>}
      <StyledEventFilterSelect
        $smallScreen={smallScreen}
        fullWidth
        multiple
        MenuProps={{
          sx: {
            maxWidth: 350,
            maxHeight: 400,
          },
          onClick: (e) => { e.stopPropagation(); },
        }}
        renderValue={(values) => (
          <Stack alignItems="flex-start" spacing={1}>
            {(values as ThingEventFilter[]).slice(0, MAX_NUMBER_OF_CHIPS).map((value, i) => (
              <StyledChip
                key={i}
                size="small"
                onDelete={() => {
                  setActiveFilters((selected) => {
                    const newValue = selected.filter(({ feature, eventTypeId }) => !(feature === value.feature && eventTypeId === value.eventTypeId));
                    return newValue;
                  });
                }}
                deleteIcon={<Icon data-testid="delete-icon" icon="CircleX" />}
                label={getSelectedLabel(value)}
              />
            ))}
            {(values as ThingEventFilter[]).length > MAX_NUMBER_OF_CHIPS && <Typography component="span" variant="body2">+{(values as ThingEventFilter[]).length - MAX_NUMBER_OF_CHIPS}</Typography>}
          </Stack>
        )}
        value={activeFilters}
      >
        {hasFilterSelectAllFlag && (
          <Box p={0}>
            <MenuItem divider>
              <SelectionCheckBox
                checked={isAllSelected}
                handleClick={toggleSelectAll}
                label={<strong>{t('common:component.filter-dropdown.labels.select-all')}</strong>}
              />
            </MenuItem>
          </Box>
        )}
        {options.map((option) => (
          <SelectionCheckBoxGroup
            key={option.feature}
            itemsChecked={activeFilters.filter(({ feature }) => feature === option.feature)?.map(({ eventTypeId }) => eventTypeId)}
            groupDisplay={option.label}
            subItemDisplay={(subItem) => getEventLabelWithFlag(option.featureTypeId, subItem, option.feature, flagUseEventV3)}
            subItems={option.events}
            updateSelectedValue={(eventTypeIds) => {
              setActiveFilters((selected) => {
                const newValue = selected.filter(({ feature }) => feature !== option.feature);
                newValue.push(...eventTypeIds.map((eventTypeId) => ({ feature: option.feature, eventTypeId, featureTypeId: option.featureTypeId })));
                return newValue;
              });
            }} />
        ))}
      </StyledEventFilterSelect>
    </FormControl>
  );
};
